import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Brain } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import ArticleCard from "@/components/ArticleCard";
import { productImg } from "@/lib/img";
import NewsletterSignup from "@/components/NewsletterSignup";

const TOPIC_KEYWORDS = {
  "Playing Time": ["playing time", "bench", "minutes", "role", "starter"],
  "Confidence": ["confidence", "belief", "slump", "nerves"],
  "Coaches": ["coach"],
  "Pressure": ["pressure", "stress", "expectation", "big game"],
  "Leadership": ["leader", "captain", "leadership"],
  "Parents": ["parent", "mom", "dad", "family"],
  "Mental Game": ["mental", "mindset", "focus", "head", "psychology"],
  "Team Culture": ["team", "culture", "locker", "chemistry", "teammate"],
};

export default function LockerRoom() {
  const [data, setData] = useState(null);
  const [topic, setTopic] = useState(null);

  useEffect(() => {
    api.get("/locker-room").then((r) => setData(r.data)).catch(() => setData({ featured: null, articles: [], topics: [], count: 0 }));
  }, []);

  if (!data) return <div className="max-w-7xl mx-auto px-4 py-24 text-[#6E6E6E]">Loading…</div>;

  const featured = data.featured;
  const rest = data.articles.filter((a) => !featured || a.slug !== featured.slug);
  const filtered = topic
    ? rest.filter((a) => {
        const hay = `${a.title} ${a.excerpt || ""}`.toLowerCase();
        return (TOPIC_KEYWORDS[topic] || []).some((k) => hay.includes(k));
      })
    : rest;

  return (
    <div data-testid="locker-room-page">
      <Seo
        title="The Locker Room"
        description="The stuff players actually talk about when the coaches leave — playing time, confidence, pressure, coaches, parents, and the mental side of youth sports."
        path="/locker-room"
      />

      {/* Hero */}
      <section className="bg-[#222222] border-b-4 border-[#DB9130]" data-testid="locker-room-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <span className="font-head uppercase tracking-[0.25em] text-xs text-[#DB9130]">A section of VoluntoldHQ</span>
          <h1 className="mt-3 font-head font-bold uppercase text-4xl sm:text-5xl lg:text-6xl text-[#F5F0EA] leading-[1.05]">
            The Locker Room
          </h1>
          <p className="mt-5 max-w-2xl text-[#C7D0D6] text-lg leading-relaxed">
            The stuff players actually talk about when the coaches leave.
          </p>
          <p className="mt-3 max-w-2xl text-[#A9B6BF] text-sm leading-relaxed">
            Playing time. Confidence. Coaches. Parents. Pressure. Leadership. The mental side. And the stuff nobody
            explains until you're already dealing with it — straight talk, both sides of the whistle.
          </p>
        </div>
      </section>

      {/* Featured */}
      {featured && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="locker-room-featured">
          <span className="kicker text-[#DB9130]">Featured</span>
          <Link to={`/article/${featured.slug}`} className="group mt-3 grid grid-cols-1 md:grid-cols-2 gap-6 items-center border-2 border-[#1E1E1E] bg-[#F3F1EC] p-4 sm:p-6 hover:border-[#DB9130] transition-colors">
            {featured.image_url && (
              <div className="aspect-[3/2] overflow-hidden bg-[#E7E3D9]">
                <img {...productImg(featured.image_url, "(min-width: 1024px) 50vw, 100vw")} alt={featured.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </div>
            )}
            <div>
              <span className="kicker text-[#6E6E6E]">{featured.category}</span>
              <h2 className="mt-1 font-head font-semibold text-2xl sm:text-3xl text-[#1E1E1E] leading-tight group-hover:text-[#2D2D2D]">{featured.title}</h2>
              <p className="mt-2 text-[#6E6E6E] leading-relaxed line-clamp-3">{featured.excerpt}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-[#2D2D2D] font-head uppercase tracking-wide text-sm">Read it <ArrowRight size={15} /></span>
            </div>
          </Link>
        </section>
      )}

      {/* Topics */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="locker-room-topics">
        <h2 className="font-head font-semibold uppercase text-xl text-[#1E1E1E] mb-4">What are you dealing with?</h2>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setTopic(null)} data-testid="locker-topic-all"
            className={`px-4 py-2 font-head uppercase tracking-wide text-xs border-2 transition-colors ${!topic ? "border-[#222222] bg-[#222222] text-[#F5F0EA]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#222222]"}`}>All</button>
          {(data.topics || []).map((t) => (
            <button key={t} onClick={() => setTopic(t)} data-testid={`locker-topic-${t.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 font-head uppercase tracking-wide text-xs border-2 transition-colors ${topic === t ? "border-[#DB9130] bg-[#DB9130] text-[#222222]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#DB9130]"}`}>{t}</button>
          ))}
        </div>
      </section>

      {/* Latest */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4" data-testid="locker-room-latest">
        <h2 className="font-head font-semibold uppercase text-xl text-[#1E1E1E] border-b-2 border-[#D3CFC0] pb-2 mb-6">
          {topic ? `${topic} — from the Locker Room` : "Latest from the Locker Room"}
        </h2>
        {filtered.length === 0 ? (
          <div className="border-2 border-[#D3CFC0] bg-[#E7E3D9] p-8 text-center" data-testid="locker-room-empty">
            <p className="font-head uppercase text-[#1E1E1E]">{topic ? "Nothing on this yet" : "First stories dropping soon"}</p>
            <p className="text-sm text-[#6E6E6E] mt-1">
              {topic ? "Try another topic — more player stories are on the way." : "Player-first stories are being written. Join the newsletter to get them first."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {filtered.map((a, i) => <ArticleCard key={a.slug} article={a} testId={`locker-article-${i}`} />)}
          </div>
        )}
      </section>

      {/* The Mental Rep + Ask Scout */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="locker-room-mental-rep">
        <div className="border-2 border-[#1E1E1E] bg-[#F3F1EC] p-6">
          <div className="flex items-center gap-2 text-[#DB9130]"><Brain size={18} /><span className="kicker">The Mental Rep</span></div>
          <h3 className="mt-2 font-head font-semibold text-xl text-[#1E1E1E]">The mental side, coached — not diagnosed.</h3>
          <p className="mt-2 text-sm text-[#6E6E6E] leading-relaxed">
            Voluntold's mental-performance angle comes from Kyto's CSPC certification — sport-psychology <em>education</em>,
            not therapy or a medical diagnosis. It's about the reps between your ears: focus, nerves, confidence, and bouncing back.
          </p>
          <Link to="/category/coaches-corner" className="mt-4 inline-flex items-center gap-1 text-[#2D2D2D] link-line font-head uppercase tracking-wide text-sm" data-testid="locker-mental-link">
            Read the mental-game pieces <ArrowRight size={14} />
          </Link>
        </div>
        <div className="border-2 border-[#D3CFC0] bg-[#E7E3D9] p-6">
          <span className="kicker text-[#6E6E6E]">Stuck on something?</span>
          <h3 className="mt-2 font-head font-semibold text-xl text-[#1E1E1E]">Ask Scout.</h3>
          <p className="mt-2 text-sm text-[#6E6E6E] leading-relaxed">
            Scout can point you to the right Voluntold read for whatever you're dealing with — benched, nervous,
            butting heads with a coach, or just trying to lead. Hit the chat button any time.
          </p>
        </div>
      </section>

      {/* Newsletter */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="locker-room-newsletter">
        <div className="border-2 border-[#222222] bg-[#222222] p-8 sm:p-10">
          <span className="font-head uppercase tracking-[0.2em] text-xs text-[#DB9130]">One read a week</span>
          <h2 className="mt-2 font-head font-semibold uppercase text-2xl text-[#F5F0EA]">Youth sports, from both sides of the whistle.</h2>
          <p className="mt-2 text-[#A9B6BF] text-sm leading-relaxed max-w-lg">
            Coaches, players, parents, culture — one worthwhile read each week. No spam.
          </p>
          <div className="mt-4 max-w-xl">
            <NewsletterSignup source="locker-room" tone="dark" showCopy={false} testId="locker-newsletter-signup" />
          </div>
        </div>
      </section>
    </div>
  );
}
